// Tarjeta "Cancelaciones" del Dashboard: total, importe perdido, antelación
// media y reparto por motivo (GET /api/stats/cancelaciones?anio=AAAA).
import React, { useEffect, useState } from 'react';
import { XCircle } from 'lucide-react';
import { api } from '../api/api';

const formatEuros = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

const CancelacionesCard = () => {
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelado = false;
    setCargando(true);
    setError(null);
    api.get('/stats/cancelaciones', { params: { anio } })
      .then(({ data }) => { if (!cancelado) setDatos(data); })
      .catch((err) => {
        console.error('Error cargando cancelaciones:', err);
        if (!cancelado) setError('No se pudieron cargar las cancelaciones.');
      })
      .finally(() => { if (!cancelado) setCargando(false); });
    return () => { cancelado = true; };
  }, [anio]);

  const anios = datos?.anios_disponibles?.length ? datos.anios_disponibles : [anio];
  const maxMotivo = Math.max(1, ...(datos?.por_motivo || []).map(m => m.total));

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-8" data-testid="dashboard-cancelaciones">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <XCircle className="w-5 h-5 text-red-500" />
          <h2 className="text-lg font-semibold text-gray-900">Cancelaciones</h2>
        </div>
        <select
          value={anio}
          onChange={(e) => setAnio(Number(e.target.value))}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-duetto-primary"
        >
          {anios.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      {error && <p className="text-sm text-red-700">{error}</p>}
      {!error && cargando && !datos && <p className="text-sm text-gray-500">Cargando...</p>}

      {!error && datos && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Eventos cancelados</p>
              <p className="text-2xl font-bold text-gray-900">{datos.total}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Importe perdido (IVA incluido)</p>
              <p className="text-2xl font-bold text-red-700">{formatEuros(datos.importe_perdido)}</p>
              {datos.importe_facturado_cancelacion > 0 && (
                <p className="text-xs text-gray-500 mt-1">Facturado por cancelación: {formatEuros(datos.importe_facturado_cancelacion)}</p>
              )}
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Antelación media</p>
              <p className="text-2xl font-bold text-gray-900">
                {datos.antelacion_media_dias != null ? `${datos.antelacion_media_dias} días` : 'Sin datos'}
              </p>
              {datos.antelacion_media_dias != null && (
                <p className="text-xs text-gray-500 mt-1">Sobre {datos.con_antelacion_registrada} cancelaciones con motivo registrado</p>
              )}
            </div>
          </div>

          {datos.por_motivo.length === 0 ? (
            <p className="text-sm text-gray-500">No hay cancelaciones en {datos.anio}.</p>
          ) : (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Por motivo</p>
              {datos.por_motivo.map(m => (
                <div key={m.motivo}>
                  <div className="flex justify-between text-xs text-gray-600 mb-0.5">
                    <span>{m.motivo}</span>
                    <span>{m.total} · {formatEuros(m.importe)}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${m.motivo === 'Sin registrar' ? 'bg-gray-400' : 'bg-red-400'}`}
                      style={{ width: `${(m.total / maxMotivo) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-gray-400 mt-4">
            Se agrupan por la fecha de cancelación. Los cancelados antiguos sin motivo registrado cuentan como "Sin registrar" y se agrupan por la fecha del evento.
          </p>
        </>
      )}
    </div>
  );
};

export default CancelacionesCard;
